import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHead from "./../general/PageHead";
import { SkeletonTable } from "./../general/Skeleton";
import { useAuth } from "./../general/AuthContext";
import { authStorage } from "../../utils/storage";
import { Search, Download, Plus, Filter, ChevronRight } from "lucide-react";

function BargainingUnits() {
  const [bargainingUnitList, setBargainingUnitList] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "LOCAL", direction: "ascending" });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedUmbrellas, setExpandedUmbrellas] = useState(new Set());
  const navigate = useNavigate();
  useAuth();
  const [showNewUnit, setShowNewUnit] = useState(false);
  const [newUnit, setNewUnit] = useState({ LOCAL: '', UNIT: '', NAME: '', TOWN: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const fetchUnits = React.useCallback(() => {
    const token = authStorage.getToken();
    if (!token) { navigate("/login"); return; }
    setLoading(true);
    fetch("/api/units", {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then(r => { if (r.status === 401) { authStorage.clearAuthData(); navigate("/login"); throw new Error("Unauthorized"); } return r.json(); })
      .then(data => {
        const sorted = [...data].sort((a, b) => {
          const la = parseFloat(a.LOCAL) || 0, lb = parseFloat(b.LOCAL) || 0;
          if (la !== lb) return la - lb;
          return (a.UNIT || 0) - (b.UNIT || 0);
        });
        setBargainingUnitList(sorted);
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, [navigate]);

  useEffect(() => { fetchUnits(); }, [fetchUnits]);

  const openNewUnit = () => { setCreateError(''); setNewUnit({ LOCAL: '', UNIT: '', NAME: '', TOWN: '' }); setShowNewUnit(true); };

  const createUnit = async () => {
    setCreateError('');
    const LOCAL = newUnit.LOCAL.trim(), NAME = newUnit.NAME.trim(), TOWN = newUnit.TOWN.trim();
    if (!LOCAL || !NAME || !TOWN) { setCreateError('Local, Name, and Town are required.'); return; }
    const token = authStorage.getToken();
    setCreating(true);
    try {
      const res = await fetch('/api/bargaining-units', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ LOCAL, UNIT: newUnit.UNIT.trim() || 0, NAME, TOWN }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setCreateError(data.error || 'Failed to create unit.'); setCreating(false); return; }
      setCreating(false);
      setShowNewUnit(false);
      if (data.id) navigate(`/unit-info/${data.id}`);
      else fetchUnits();
    } catch (e) {
      console.error('Create unit failed', e);
      setCreateError('Failed to create unit.');
      setCreating(false);
    }
  };

  const compareUnits = (a, b, key, direction) => {
    const numericKeys = ['LOCAL', 'UNIT'];
    let aVal = a[key], bVal = b[key];
    if (numericKeys.includes(key)) { aVal = parseFloat(aVal) || 0; bVal = parseFloat(bVal) || 0; }
    else { aVal = (aVal || '').toString().toLowerCase(); bVal = (bVal || '').toString().toLowerCase(); }
    if (aVal < bVal) return direction === 'ascending' ? -1 : 1;
    if (aVal > bVal) return direction === 'ascending' ? 1 : -1;
    if (key !== 'LOCAL') { const la = parseFloat(a.LOCAL) || 0, lb = parseFloat(b.LOCAL) || 0; if (la !== lb) return la - lb; }
    if (key !== 'UNIT') return ((a.UNIT || 0) - (b.UNIT || 0)) * (direction === 'ascending' ? 1 : -1);
    return 0;
  };

  const sortData = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "ascending" ? "descending" : "ascending";
    setSortConfig({ key, direction });
    setBargainingUnitList(list => [...list].sort((a, b) => compareUnits(a, b, key, direction)));
  };

  const currentUserName = sessionStorage.getItem('fullName') || sessionStorage.getItem('username') || '';

  const checkMyUnit = (unit) => {
    if (!currentUserName) return false;
    const nameParts = currentUserName.split(' ').filter(p => p.length > 0);
    const checkMatch = (staffName) => {
      if (!staffName) return false;
      const sl = staffName.toLowerCase().trim();
      const ul = currentUserName.toLowerCase().trim();
      if (sl.includes(ul)) return true;
      if (nameParts.length === 2) {
        const rev = `${nameParts[1]}, ${nameParts[0]}`.toLowerCase();
        if (sl.includes(rev)) return true;
      }
      return nameParts.some(p => p.length > 2 && sl.includes(p.toLowerCase()));
    };
    return checkMatch(unit.STAFF_ASSIGNED) || checkMatch(unit.SECOND_STAFF_ASSIGNED);
  };

  const filteredUnits = bargainingUnitList.filter(unit => {
    if (filter === "assigned") {
      if (!unit.STAFF_ASSIGNED && !unit.SECOND_STAFF_ASSIGNED) return false;
    } else if (filter === "unassigned") {
      if (unit.STAFF_ASSIGNED || unit.SECOND_STAFF_ASSIGNED) return false;
    } else if (filter === "recent") {
      if (!checkMyUnit(unit)) return false;
    }
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (unit.NAME && unit.NAME.toLowerCase().includes(s)) ||
      (unit.TOWN && unit.TOWN.toLowerCase().includes(s)) ||
      (unit.LOCAL && unit.LOCAL.toString().includes(s)) ||
      (unit.UNIT && unit.UNIT.toString().includes(s)) ||
      (unit.STAFF_ASSIGNED && unit.STAFF_ASSIGNED.toLowerCase().includes(s)) ||
      (unit.SECOND_STAFF_ASSIGNED && unit.SECOND_STAFF_ASSIGNED.toLowerCase().includes(s))
    );
  });

  const handleSearchChange = React.useCallback(e => setSearchTerm(e.target.value), []);

  const toggleUmbrella = (id) => {
    setExpandedUmbrellas(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const chapterCounts = React.useMemo(() => {
    const counts = {};
    bargainingUnitList.forEach(u => {
      if (u.umbrella_parent_id) counts[u.umbrella_parent_id] = (counts[u.umbrella_parent_id] || 0) + 1;
    });
    return counts;
  }, [bargainingUnitList]);

  const displayUnits = React.useMemo(() => {
    if (searchTerm) return filteredUnits;
    return filteredUnits.filter(u => !u.umbrella_parent_id || expandedUmbrellas.has(u.umbrella_parent_id));
  }, [filteredUnits, expandedUmbrellas, searchTerm]);

  const sortIndicator = (col) => sortConfig.key === col ? (sortConfig.direction === 'ascending' ? ' ▲' : ' ▼') : '';

  const newUnitInputStyle = { width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontWeight: 400 };

  return (
    <>
      <PageHead
        title="Bargaining units"
        sub={`${bargainingUnitList.length} units across Connecticut`}
        actions={
          <>
            <button className="btn btn-secondary"><Download size={15} /> Export</button>
            <button className="btn btn-primary" onClick={openNewUnit}><Plus size={15} /> New unit</button>
          </>
        }
      />

      {showNewUnit && (
        <div
          onClick={() => !creating && setShowNewUnit(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(460px, 92vw)', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>New bargaining unit</h2>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 13 }}>Local, Name, and Town are required.</p>
            <form onSubmit={e => { e.preventDefault(); createUnit(); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Local *
                  <input autoFocus value={newUnit.LOCAL} onChange={e => setNewUnit(u => ({ ...u, LOCAL: e.target.value }))} placeholder="e.g. 1303" style={newUnitInputStyle} />
                </label>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Unit
                  <input value={newUnit.UNIT} onChange={e => setNewUnit(u => ({ ...u, UNIT: e.target.value }))} placeholder="e.g. 27" style={newUnitInputStyle} />
                </label>
              </div>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginTop: 12 }}>Name *
                <input value={newUnit.NAME} onChange={e => setNewUnit(u => ({ ...u, NAME: e.target.value }))} placeholder="Unit name" style={newUnitInputStyle} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginTop: 12 }}>Town *
                <input value={newUnit.TOWN} onChange={e => setNewUnit(u => ({ ...u, TOWN: e.target.value }))} placeholder="Town" style={newUnitInputStyle} />
              </label>
              {createError && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{createError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewUnit(false)} disabled={creating}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creating…' : 'Create unit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="list-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'nowrap' }}>
        <div className="pills" style={{ flexShrink: 0 }}>
          <button className={"pill" + (filter === "all" ? " active" : "")} onClick={() => setFilter("all")}>All</button>
          <button className={"pill" + (filter === "assigned" ? " active" : "")} onClick={() => setFilter("assigned")}>Assigned</button>
          <button className={"pill" + (filter === "unassigned" ? " active" : "")} onClick={() => setFilter("unassigned")}>Unassigned</button>
          <button className={"pill" + (filter === "recent" ? " active" : "")} onClick={() => setFilter("recent")}>My Units</button>
        </div>
        <div className="list-toolbar-right" style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', flexShrink: 0 }}>
          <div className="search-box">
            <Search size={15} className="ic" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, local, town…"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
          <button className="btn btn-secondary btn-sm"><Filter size={14} /> Filters</button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: '20px 22px' }}>
            <SkeletonTable rows={10} columns={8} />
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th onClick={() => sortData('LOCAL')} style={{ cursor: 'pointer', userSelect: 'none' }}>Local{sortIndicator('LOCAL')}</th>
                    <th onClick={() => sortData('NAME')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name{sortIndicator('NAME')}</th>
                    <th onClick={() => sortData('TOWN')} style={{ cursor: 'pointer', userSelect: 'none' }}>Town{sortIndicator('TOWN')}</th>
                    <th>Type</th>
                    <th onClick={() => sortData('STAFF_ASSIGNED')} style={{ cursor: 'pointer', userSelect: 'none' }}>Staff Rep{sortIndicator('STAFF_ASSIGNED')}</th>
                    <th className="num">Active Jobs</th>
                    <th className="num">Dues Payers</th>
                    <th>Audit</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayUnits.map(unit => {
                    const isParent = unit.is_umbrella === 1;
                    const isChapter = !!unit.umbrella_parent_id;
                    const isExpanded = expandedUmbrellas.has(unit.id);
                    const count = chapterCounts[unit.id] || 0;

                    if (isParent) {
                      return (
                        <tr key={unit.id} className="umbrella-parent-row" onClick={() => toggleUmbrella(unit.id)}>
                          <td colSpan={9} className="umbrella-parent-cell">
                            <div className="umbrella-parent-inner">
                              <span className="umbrella-arrow">{isExpanded ? '▾' : '▸'}</span>
                              <span className="umbrella-local">Local {unit.LOCAL}-{String(unit.UNIT || 0).padStart(4, '0')}</span>
                              <span className="umbrella-name">
                                <Link to={`/unit-info/${unit.id}`} className="umbrella-link" onClick={e => e.stopPropagation()}>{unit.NAME}</Link>
                              </span>
                              <span className="umbrella-count">{count} chapter{count !== 1 ? 's' : ''}</span>
                              {unit.sector && unit.sector !== 'Public' && (
                                <span className={`sector-badge ${unit.sector}`}>{unit.sector}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const unitType = unit.UNIT_TYPE || unit.TYPE || unit.sector || "Municipal";
                    const staffRep = [unit.STAFF_ASSIGNED, unit.SECOND_STAFF_ASSIGNED].filter(Boolean).join(', ');

                    return (
                      <tr
                        key={unit.id}
                        className={`clickable${isChapter ? ' umbrella-chapter-row' : ''}`}
                        onClick={() => navigate(`/unit-info/${unit.id}`)}
                      >
                        <td className="card-title" style={{ fontWeight: 500, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {unit.NAME}
                          {unit.sector && unit.sector !== 'Public' && (
                            <span className={`sector-badge ${unit.sector}`}>{unit.sector}</span>
                          )}
                        </td>
                        <td className="mono" data-label="Local" style={{ whiteSpace: 'nowrap' }}>
                          {isChapter && <span className="chapter-indent">↳</span>}
                          {unit.LOCAL}-{String(unit.UNIT || 0).padStart(4, '0')}
                        </td>
                        <td data-label="Town" style={{ whiteSpace: 'nowrap' }}>
                          {unit.TOWN && (
                            <span
                              style={{ color: 'var(--fg-link)', cursor: 'pointer' }}
                              onClick={e => { e.stopPropagation(); navigate(`/town/${unit.TOWN}`); }}
                            >
                              {unit.TOWN}
                            </span>
                          )}
                        </td>
                        <td data-label="Type" style={{ whiteSpace: 'nowrap' }}>
                          <span className="badge badge-neutral">{unitType}</span>
                        </td>
                        <td data-label="Staff Rep" style={{ color: 'var(--fg-2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{staffRep || <span style={{ color: 'var(--fg-3)' }}>—</span>}</td>
                        <td data-label="Active Jobs" className="num">{unit.ActiveJobCount != null ? unit.ActiveJobCount : <span style={{ color: 'var(--fg-3)' }}>—</span>}</td>
                        <td data-label="Dues Payers" className="num">{unit.TotalDuesPayers != null ? unit.TotalDuesPayers : <span style={{ color: 'var(--fg-3)' }}>—</span>}</td>
                        <td data-label="Audit" style={{ whiteSpace: 'nowrap' }}>{unit.AuditCompleteOverview != null ? unit.AuditCompleteOverview : <span style={{ color: 'var(--fg-3)' }}>—</span>}</td>
                        <td className="row-chevron" style={{ width: 32, color: 'var(--fg-3)' }}><ChevronRight size={15} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 22px', borderTop: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--fg-2)' }}>
              <span>Showing <b style={{ color: 'var(--fg-1)' }}>{filteredUnits.length}</b> of {bargainingUnitList.length}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default BargainingUnits;
